import { useRef, useState } from "react";
import { imageAspectRatio, type DocumentImageSizes } from "./documentImage";

/**
 * Everything the image-document lightbox needs to open and close, out of the card.
 *
 * WHY IT IS A HOOK. `CongressImageCard` is a business component, and the webapp
 * bans `useState`/`useRef` there — the escape the rules name is a dedicated hook,
 * the same shape as `useDebouncedValue` or `useLocationState`. An earlier revision
 * kept this inline with a comment arguing that overlay disclosure is UI machinery
 * like a Modal's; that argument holds for a design-system primitive and not for a
 * feature card, and a review caught it. The card is now a pure render plus
 * callbacks again, and the transition's state has one owner.
 *
 * The ref is here too, because the two measurements it exists for are part of the
 * same lifecycle: the intrinsic ratio at open, and the card's box at close.
 */
export function useDocumentLightbox(imageSizes: DocumentImageSizes) {
  const imageRef = useRef<HTMLImageElement>(null);
  const [open, setOpen] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [exitInPlace, setExitInPlace] = useState(false);

  /**
   * The intrinsic ratio, preferring the wire over the DOM.
   *
   * The `sizes` renditions all carry width+height and all share the original's
   * ratio. Measuring the card's own image is the fallback for a null `sizes`, and
   * it is honest rather than a guess: that image is on screen, so it is decoded.
   * Read at tap, never during render.
   */
  const measureAspectRatio = (): number | null => {
    const fromSizes = imageAspectRatio(imageSizes);
    if (fromSizes !== null) return fromSizes;
    const element = imageRef.current;
    if (element === null || element.naturalHeight === 0) return null;
    return element.naturalWidth / element.naturalHeight;
  };

  const openLightbox = () => {
    setAspectRatio(measureAspectRatio());
    setExitInPlace(false);
    setOpen(true);
  };

  const closeLightbox = () => {
    const element = imageRef.current;
    const box = element === null ? null : element.getBoundingClientRect();
    // Scroll is locked while the overlay is open, so the card that was tapped is
    // normally still where it was. It can still go missing — a background refetch
    // can reorder or drop the block underneath the overlay — and a reverse morph
    // to a box outside the viewport reads as the image flying away, a different
    // failure from the one being fixed. Fully outside is what counts: a partly
    // visible card still gives the reader something to follow.
    const onScreen =
      box !== null && box.bottom > 0 && box.top < window.innerHeight;
    setExitInPlace(!onScreen);
    // One frame between "decide the exit" and "start it". AnimatePresence renders
    // the child it last saw, so setting the mode and unmounting in one commit
    // would exit with the previous mode. Changing the mode while the overlay is
    // open is visually inert, which is what makes the extra frame free.
    requestAnimationFrame(() => {
      setOpen(false);
    });
  };

  return {
    imageRef,
    open,
    aspectRatio,
    exitInPlace,
    openLightbox,
    closeLightbox,
  };
}
